import React, { Component } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import Loading from './Loading';

export const IGNORE_STRATEGY = 'IGNORE';
export const UPDATE_STRATEGY = 'UPDATE';
export const MERGE_STRATEGY = 'MERGE';

const MAX_IMPORT_FILE_SIZE_MB = 5;
const MAX_IMPORT_FILE_SIZE_BYTES = MAX_IMPORT_FILE_SIZE_MB * 1024 * 1024;

const CSV_TEMPLATE = `System Name,Language Code,Name,Description,Calories,Calories From,Calories To
example-burger,en-gb,Example Burger,Classic beef burger,500,,
example-salad,en-gb,Example Salad,Fresh garden salad,,200,300`;

const styles = theme => ({
  section: {
    marginBottom: '16px'
  },
  hiddenInput: {
    display: 'none'
  },
  fileName: {
    marginTop: '8px',
    color: 'rgba(0, 0, 0, 0.54)'
  },
  helperText: {
    marginTop: '4px',
    color: 'rgba(0, 0, 0, 0.54)'
  },
  templateLink: {
    marginTop: '8px'
  },
  errorText: {
    marginTop: '16px',
    color: theme.palette.error.main
  },
  errorContainer: {
    maxHeight: '300px',
    overflowY: 'auto'
  },
  errorList: {
    margin: '8px 0 0',
    paddingLeft: '20px'
  },
  optionLabel: {
    display: 'block'
  },
  loading: {
    margin: '16px auto'
  }
});

class ImportContentModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      file: null,
      existingStrategy: IGNORE_STRATEGY,
      fileError: ''
    };
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      this.setState({
        file: null,
        existingStrategy: IGNORE_STRATEGY,
        fileError: ''
      });
    }
  }

  handleFileChange = event => {
    const file = event.target.files[0] || null;
    this.setState({ file, fileError: '' });
  };

  handleStrategyChange = event => {
    this.setState({ existingStrategy: event.target.value });
  };

  handleDownloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'import-content-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  handleConfirm = () => {
    const { file, existingStrategy } = this.state;
    if (!file) {
      this.setState({ fileError: 'Select a CSV file to import' });
      return;
    }
    if (file.size > MAX_IMPORT_FILE_SIZE_BYTES) {
      this.setState({
        fileError: `File is too large. Maximum size is ${MAX_IMPORT_FILE_SIZE_MB} MB.`
      });
      return;
    }
    this.props.onConfirm(file, existingStrategy);
  };

  renderErrors = () => {
    const { errors, classes } = this.props;
    if (!errors || errors.length === 0) {
      return null;
    }
    return (
      <div id="import-content-errors" className={classes.errorContainer}>
        <Typography variant="body2" className={classes.errorText}>
          The file was not imported. Fix the following and try again:
        </Typography>
        <ul className={classes.errorList}>
          {errors.map((error, index) => (
            <li key={`${error.rowNumber}-${index}`}>
              {error.rowNumber ? `Row ${error.rowNumber}: ` : ''}
              {error.reason}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  render() {
    const { open, importing, onCancel, classes } = this.props;
    const { file, existingStrategy, fileError } = this.state;

    return (
      <Dialog
        open={open}
        onClose={onCancel}
        aria-labelledby="import-content-dialog-title"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="import-content-dialog-title">
          Import Contents
        </DialogTitle>
        <DialogContent>
          <div className={classes.section}>
            <Button
              id="btn-select-csv"
              disabled={importing}
              variant="outlined"
              color="primary"
              component="label"
            >
              Select CSV File
              <input
                type="file"
                accept=".csv"
                onChange={this.handleFileChange}
                className={classes.hiddenInput}
              />
            </Button>
            <Typography
              id="import-content-file-name"
              variant="body2"
              className={classes.fileName}
            >
              {file ? file.name : 'No file selected'}
            </Typography>
            <Typography
              id="import-content-max-size"
              variant="caption"
              className={classes.helperText}
            >
              Maximum file size: {MAX_IMPORT_FILE_SIZE_MB} MB
            </Typography>
            {fileError && (
              <Typography
                id="import-content-file-error"
                variant="body2"
                className={classes.errorText}
              >
                {fileError}
              </Typography>
            )}
          </div>
          <FormControl component="fieldset" disabled={importing}>
            <FormLabel component="legend">When the item exists</FormLabel>
            <RadioGroup
              value={existingStrategy}
              onChange={this.handleStrategyChange}
            >
              <FormControlLabel
                value={IGNORE_STRATEGY}
                control={<Radio id="strategy-ignore" color="primary" />}
                label={
                  <Tooltip title="Skips Content Items whose System Name already exists.">
                    <span className={classes.optionLabel}>Ignore</span>
                  </Tooltip>
                }
              />
              <FormControlLabel
                value={UPDATE_STRATEGY}
                control={<Radio id="strategy-update" color="primary" />}
                label={
                  <Tooltip title="Replaces the Content Item with the CSV contents, removing translations not in the CSV.">
                    <span className={classes.optionLabel}>Update</span>
                  </Tooltip>
                }
              />
              <FormControlLabel
                value={MERGE_STRATEGY}
                control={<Radio id="strategy-merge" color="primary" />}
                label={
                  <Tooltip title="Keeps translations not present in the CSV and overwrites the languages the CSV provides.">
                    <span className={classes.optionLabel}>Merge</span>
                  </Tooltip>
                }
              />
            </RadioGroup>
          </FormControl>
          {importing && (
            <Loading size={32} delay="0" className={classes.loading} />
          )}
          {!importing && this.renderErrors()}
          <div className={classes.templateLink}>
            <Link
              id="import-content-template-link"
              component="button"
              variant="body2"
              onClick={this.handleDownloadTemplate}
            >
              Download CSV template
            </Link>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            id="btn-import-cancel"
            onClick={onCancel}
            disabled={importing}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            id="btn-import-confirm"
            onClick={this.handleConfirm}
            disabled={importing}
            color="primary"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default withStyles(styles)(ImportContentModal);
